<template>
  <CheckoutEventStateView
    v-if="checkoutStore.eventStateData"
    :data="checkoutStore.eventStateData"
  />

  <div v-else-if="checkoutStore.eventState === 'loading'" class="min-h-screen">
    <BaseContainer>
      <p class="py-16 md:py-24 text-center text-text">
        {{ $t('checkout.event_state.loading') }}
      </p>
    </BaseContainer>
  </div>

  <div class="mb-16" v-else-if="eventID && checkoutStore.eventState === 'available'">
    <CheckoutHeader :current-step="1" />

    <CheckoutLayout
      :next-link="nextLink"
      :title="$t('checkout.choose_tickets')"
      :has-discount-code="true"
      @on-code-change="handleCodeChange"
    >
      <template #header>
        <CheckoutTicketHeader/>
      </template>

      <!-- <div
        v-if="!cart.items.length"
        class="bg-surface-grey-light p-4 rounded-sm flex gap-4 font-semibold"
      >
        <CartIcon class="h-6 w-6 fill-primary" />
        {{ $t('globals.cart_empty') }}
      </div> -->
      
      <div class="space-y-4">
        <BaseSlideTransition>
          <CheckoutTicketsNotice v-if="checkoutStore.noticeTickets.length > 0 && checkoutStore.vouchercode && checkoutStore.vouchercode.code && checkoutStore.vouchercode.code.length > 0">
            <span v-if="checkoutStore.noticeTickets.length > 1">
              {{
                $t('checkout.extra_tickets_shown', {
                  count: checkoutStore.noticeTickets.length,
                })
              }}
            </span>
            <span v-else>
              {{
                $t('checkout.extra_ticket_shown')
              }}
            </span>
            <span class="ml-1" v-if="checkoutStore.vouchercode && checkoutStore.vouchercode.code && checkoutStore.vouchercode.code.length > 0">
              {{ checkoutStore.vouchercode.code }}
            </span>
          </CheckoutTicketsNotice>
        </BaseSlideTransition>

        <CheckoutTicket
          v-for="ticket in checkoutStore.displayTickets"
           :key="ticket.id"
           :ticket="ticket"
           :event="checkoutStore.event"
           :eventID="eventID"
        />
      </div>
    </CheckoutLayout>
  </div>
</template>

<script setup lang="ts">
  import { useHead } from '@unhead/vue';
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';

  import { fetchEvent } from '@/api/event';
  import CheckoutHeader from '@/components/checkout/CheckoutHeader.vue';
  import CheckoutLayout from '@/components/checkout/CheckoutLayout.vue';
  import CheckoutTicket from '@/components/checkout/CheckoutTicket.vue';
  import CheckoutTicketHeader from '@/components/checkout/CheckoutTicketHeader.vue';
  import CheckoutTicketsNotice from '@/components/checkout/CheckoutTicketsNotice.vue';
  import BaseContainer from '@/components/ui/BaseContainer.vue';
  import BaseSlideTransition from '@/components/ui/BaseSlideTransition.vue';
  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { useSsoStore } from '@/stores/sso';
  import { useToastStore } from '@/stores/toastStore';
  import type { CodeItem } from '@/types';
  import { trackBeginCheckout } from '@/utils/analytics';
  import { useCheckoutNavigation } from '@/utils/checkoutNavigation';
  import {
    DISCOUNT_STORAGE_KEY,
    loadCodesFromStorage,
  } from '@/utils/loadCodesFromLocalStorage';
  import { slugify } from '@/utils/slugify';
  import CheckoutEventStateView from '@/views/CheckoutEventStateView.vue';
  import { useWebsocketStore } from '@/websocket/websocket';

  const { t } = useI18n();
  const router = useRouter();
  const route = useRoute();
  const websocketStore = useWebsocketStore();
  const checkoutStore = useCheckoutStore();

  onBeforeRouteLeave(() => {
    cart.cleanUpEmptyEvents(true);
  });
  const cart = useCartStore();
  const subscribeEventSent = ref(false);
  const toastStore = useToastStore();
  const { getNextRoute } = useCheckoutNavigation();
  const vouchercodeAutoApplied = ref(false);
  const beginCheckoutTracked = ref(false);
  let subscriptionTimeout: number | undefined;
  let boundaryTimeout: number | undefined;

  // Load all URL query params into the checkout store
  checkoutStore.loadUrlParams();

  // Get event ID from route params (primary source), sync to store
  const routeId = route.params.id as string | undefined;
  if (routeId) {
    checkoutStore.setCheckoutID(routeId);
  } else if (!checkoutStore.getCheckoutID()) {
    // Fallback: check URL query param ?id= (legacy)
    const urlId = checkoutStore.getUrlParam('id');
    if (urlId) {
      checkoutStore.setCheckoutID(urlId);
    }
  }

  const eventID = computed(() => checkoutStore.getCheckoutID());

  function initEvent(id: number | string) {
    clearEventTimers();
    checkoutStore.setCheckoutEventLoading();
    if (!subscribeEventSent.value) {
      websocketStore.send('subscribeevent', { id }, undefined);
      subscribeEventSent.value = true;
    }

    subscriptionTimeout = window.setTimeout(() => {
      if (checkoutStore.eventState !== 'loading') return;
      checkoutStore.setCheckoutEventState({
        id,
        state: 'error',
        server_time: Math.floor(Date.now() / 1000),
      });
    }, 15000);
  }

  if (eventID.value) {
    initEvent(eventID.value);
  } else {
    // in case of no eventID, route to home
    window.location.href = '/';
  }

  watch(() => checkoutStore.checkoutID, (newID) => {
    if (newID) {
      subscribeEventSent.value = false;
      initEvent(newID);
      // Update URL to reflect the new event
      const isClosedEvent = typeof newID === 'string' && /^[0-9a-f]{96}$/.test(newID);
      const params: Record<string, string | number> = { id: newID };
      if (!isClosedEvent) {
        const cartItem = cart.items.find(item => item.id == newID);
        params.name = cartItem?.name ? slugify(cartItem.name) : 'event';
      }
      router.replace({
        name: 'checkout-tickets',
        params,
        query: route.query,
      });
    }
  });

  const nextLink = computed(() => getNextRoute('checkout-tickets') ?? '/checkout/information');

  // Event level "sso required": the event page blocks the button, but the checkout address can be opened directly.
  const ssoStore = useSsoStore();
  async function checkSsoAccess() {
    const id = eventID.value;
    if (!id) return;
    await ssoStore.whenReady();
    // Do not guess when the login status is not known (slow or failed request)
    if (!ssoStore.isActive || ssoStore.isLoading) return;

    const event = await fetchEvent(id);
    if (event && !ssoStore.canAccessEvent(event)) {
      const isClosedEvent = typeof id === 'string' && /^[0-9a-f]{96}$/.test(id);
      router.replace({
        name: 'event-detail',
        params: isClosedEvent ? { id } : { id, name: slugify(event.info1) },
      });
    }
  }
  checkSsoAccess();

  function clearEventTimers() {
    if (subscriptionTimeout !== undefined) window.clearTimeout(subscriptionTimeout);
    if (boundaryTimeout !== undefined) window.clearTimeout(boundaryTimeout);
    subscriptionTimeout = undefined;
    boundaryTimeout = undefined;
  }

  function scheduleBoundaryResubscribe(boundary: number | undefined, serverTime: number | null) {
    console.debug('Checkout sale boundary check', {
      eventId: eventID.value,
      boundary,
      serverTime,
      boundaryPassed: boundary !== undefined && serverTime !== null && boundary <= serverTime,
    });
    if (!boundary || !serverTime || boundary <= serverTime) return;

    const deadline = Date.now() + (boundary - serverTime) * 1000 + 250;
    const maxTimeoutMs = 24 * 60 * 60 * 1000;
    const checkBoundary = () => {
      const remainingMs = deadline - Date.now();
      if (remainingMs <= 0) {
        if (!eventID.value) return;
        subscribeEventSent.value = false;
        initEvent(eventID.value);
        return;
      }

      boundaryTimeout = window.setTimeout(checkBoundary, Math.min(remainingMs, maxTimeoutMs));
    };

    checkBoundary();
  }

  watch(
    () => [checkoutStore.eventState, checkoutStore.eventStateData] as const,
    ([state, stateData]) => {
      if (state === 'loading') return;
      if (subscriptionTimeout !== undefined) window.clearTimeout(subscriptionTimeout);
      subscriptionTimeout = undefined;
      if (boundaryTimeout !== undefined) window.clearTimeout(boundaryTimeout);
      boundaryTimeout = undefined;

      if (state === 'not_started') {
        scheduleBoundaryResubscribe(stateData?.salestart, stateData?.server_time ?? null);
      } else if (state === 'available') {
        scheduleBoundaryResubscribe(checkoutStore.event.saleend, checkoutStore.eventServerTime);
      }
    },
  );

  onBeforeUnmount(clearEventTimers);

  

  useHead({
    title: t('checkout.choose_tickets'),
    meta: [
      {
        property: 'og:title',
        content: t('checkout.choose_tickets'),
      },
    ],
  });

  const activeCodes = ref<CodeItem[]>([]);
  //const noticeTickets = ref<Ticket[]>([]);

  onMounted(() => {
    websocketStore.open();
    if( eventID.value && !subscribeEventSent.value ){
      websocketStore.send('subscribeevent', { id: eventID.value }, undefined);
      subscribeEventSent.value = true;
    }
    // Track begin_checkout when user enters the tickets page with items in cart
    if (cart.items.length > 0) {
      trackBeginCheckout(cart.items);
      beginCheckoutTracked.value = true;
    } else {
      // Cart is empty on mount — watch for first item to be added
      const stopWatch = watch(() => cart.items.length, (newLength) => {
        if (newLength > 0 && !beginCheckoutTracked.value) {
          trackBeginCheckout(cart.items);
          beginCheckoutTracked.value = true;
          stopWatch();
        }
      });
    }
  });

  function loadTickets() {
    activeCodes.value = loadCodesFromStorage(DISCOUNT_STORAGE_KEY);
    /*
    const newTickets = ticketItems.filter(ticket => {
      if (!ticket.activationCode) return true;

      return activeCodes.value.some(
        code =>
          code.code.toLowerCase() === ticket.activationCode?.toLocaleLowerCase()
      );
    });

    noticeTickets.value = newTickets.filter(
      ticket =>
        ticket.activationCode &&
        !initialTickets.value.some(x => x.id === ticket.id)
    );*/ // Disabled for now - always show all tickets, vouchercode handling here

    //tickets.value = newTickets;
  }

  function handleCodeChange() {
    loadTickets();
  }

  //Watch cart.differentCount and if it changes to true, show a toast informing the user they could not reserve the specified amount
  watch(() => cart.differentCount, newValue => {
    if (newValue) {
      const message = cart.differentCountMessage.message;
      const count = cart.differentCountMessage.count;
      const ticketsText = (count == 1 ? t('common.ticket') : t('common.ticket_plural')).toLocaleLowerCase();
      console.log('Cart differentCount is true, showing toast with message:', message, 'and count:', count);
      if(message && message == 'initial'){
        toastStore.showToast(t('checkout.reservation_mismatch', {tickets: ticketsText}), 15000);
      }
      else if(message && count){
        toastStore.showToast(t('checkout.reservation_mismatch_' + message, { count, tickets: ticketsText }), 15000);
      }
      cart.differentCount = false; // reset the flag
    }
  });

  // Auto-apply vouchercode from URL param once event data confirms voucher codes are enabled
  watch(() => checkoutStore.event?.has_vouchercodes, (hasVouchercodes) => {
    if (hasVouchercodes == 1 && !vouchercodeAutoApplied.value) {
      const vouchercodeParam = checkoutStore.getUrlParam('vouchercode');
      if (vouchercodeParam) {
        vouchercodeAutoApplied.value = true;
        websocketStore.send('vouchercode', { code: vouchercodeParam }, undefined);
        // Remove vouchercode from URL to prevent re-application on refresh
        const query = { ...router.currentRoute.value.query };
        delete query.vouchercode;
        router.replace({ query });
      }
    }
  }, { immediate: true });

  onMounted(() => {
    //tickets.value = ticketItems.filter(ticket => !ticket.activationCode); // This is for vouchercodes

    setTimeout(() => {
      loadTickets();
    }, 0);
    loadTickets();
  });
</script>
